import { useCallback, useMemo } from "react";

import type { FormatMessages } from "@skilitics/localization";
import { useFormatMessage } from "@skilitics/localization";
import {
  type ImportTarget,
  type PortableDesignerNodeInContext,
  assert,
  getNodeSlotIdSelector,
  portableDesignerNodeInContextSchema,
  SlotPathError,
} from "@skilitics/nautilus-core-designer-data";

import { useContentHostId } from "../backend/index.ts";
import { TemplateImportError } from "../errors/index.ts";
import type { NodeSelectionCriteria } from "../library/index.ts";
import { useMutateNodeDesigns, useNautilusSelector } from "../state/index.ts";
import type { CanImportResult } from "../state/useResolveNodeLinkagesBlockingMoveToSlot.ts";
import { useCanImportHere } from "./useCanImportHere.ts";
import { useImportNodeDesign } from "./useImportNodeDesign.tsx";

export type SlotPasteClipboardOptions = {
  target: ImportTarget;
  criteria: NodeSelectionCriteria;
} & Parameters<typeof useImportNodeDesign>[0] &
  Pick<Parameters<typeof useCanImportHere>[0], "formatStringOverrides" | "targetLayout">;

export const useSlotPasteClipboard = ({
  target,
  criteria,
  targetLayout,
  formatStringOverrides,
  ...importOptions
}: SlotPasteClipboardOptions) => {
  const formatMessage = useFormatMessage(formatStrings);
  const hostId = useContentHostId();
  const getSlotIdById = useNautilusSelector(getNodeSlotIdSelector);
  const { move, replace, addMultilingual } = useMutateNodeDesigns();
  const canImportHere = useCanImportHere({
    target,
    criteria,
    targetLayout,
    formatStringOverrides,
  });
  const importNodeDesign = useImportNodeDesign(importOptions);

  const canPaste = useCallback(async (): Promise<CanImportResult> => {
    if (!document.hasFocus()) {
      return { value: false, message: "" };
    }

    // Check browser clipboard permission first (not all browsers support this)
    if (navigator.permissions) {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: "clipboard-read" as PermissionName,
        });

        if (permissionStatus.state === "denied") {
          return {
            value: false,
            message: formatMessage("clipboardPermissionDenied"),
          };
        }
      } catch (err) {
        // Some browsers (like Firefox) don't support clipboard-read permission query
        // We'll proceed with attempting to read the clipboard directly
      }
    }

    try {
      const text = await navigator.clipboard.readText();
      let jsonText;
      try {
        jsonText = JSON.parse(text);
      } catch (err) {
        return { value: false, message: formatMessage("resultNotNode") };
      }
      const parsed = portableDesignerNodeInContextSchema.parse(jsonText);
      if (parsed.source.appVersion !== APP_VERSION) {
        return { value: false, message: formatMessage("versionMismatch") };
      }
      return canImportHere(parsed);
    } catch (err) {
      return { value: false, message: formatMessage("errorPasting") };
    }
  }, [canImportHere, formatMessage]);

  return useMemo(
    () => ({
      paste: (onBlocked?: (res: Exclude<CanImportResult, boolean>) => void) => {
        void (async () => {
          try {
            const text = await navigator.clipboard.readText();
            let spec: PortableDesignerNodeInContext;
            try {
              const result = portableDesignerNodeInContextSchema.safeParse(JSON.parse(text));
              if (!result.success) {
                console.warn(`Clipboard content is not a valid portable node: ${result.error.message}`);
                console.warn(result.error);
                onBlocked?.({ value: false, message: formatMessage("resultNotNode") });
                return;
              }
              spec = result.data;
            } catch (err) {
              onBlocked?.({ value: false, message: formatMessage("errorPasting") });
              return;
            }
            const acceptable = canImportHere(spec);
            if (acceptable !== true) {
              onBlocked?.(acceptable);
              return;
            }
            const { source, node, linkages, version } = spec;
            if (source.action === "cut" && source.hostId === hostId) {
              if ("slotId" in target) {
                move(source.slotId, target.slotId);
              } else {
                const targetSlotId = getSlotIdById(target.slotResidentNodeId);
                assert(targetSlotId);
                move(source.slotId, targetSlotId);
              }
              // clear from clipboard as can't cut again (moved)
              try {
                await navigator.clipboard.writeText("");
              } finally {
                window.dispatchEvent(new ClipboardEvent("paste"));
              }
            } else {
              const importedNode = await importNodeDesign(spec, target);
              if ("slotId" in target) {
                addMultilingual(importedNode, target.slotId);
              } else {
                replace(target.slotResidentNodeId, importedNode);
              }
              window.dispatchEvent(new ClipboardEvent("paste"));
            }
          } catch (err) {
            console.error({ err });

            let message = formatMessage("errorPasting");

            if (err instanceof TemplateImportError) {
              message = err.cause as string;
            } else if (err instanceof SlotPathError) {
              // slot path resolution has been canceled by the user
              return;
            }

            onBlocked?.({ value: false, message });
          }
        })();
      },
      canPaste,
    }),
    [
      canPaste,
      canImportHere,
      hostId,
      formatMessage,
      target,
      move,
      getSlotIdById,
      importNodeDesign,
      addMultilingual,
      replace,
    ],
  );
};

const formatStrings = {
  resultNotNode: {
    en: "There's nothing to paste. Copy an item first, then try again.",
  },
  nothingInClipboard: {
    en: "There's nothing to paste.",
  },
  versionMismatch: {
    en: "This was copied from an older version. Refresh the page and copy it again.",
  },
  errorPasting: {
    en: "Something went wrong while pasting. Please try again.",
  },
  clipboardPermissionDenied: {
    en: "Your browser blocked clipboard access. Please check your browser settings.",
  },
} satisfies FormatMessages;
